import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../components/AuthContext';
import { itemLabel, itemUom, trackingSummary } from '../../lib/requestItems';
import { topApprovers, approverPhrase, knowsVerb, canWithdrawMaterialRequest } from '../../lib/canApprove';
// #514 / gate D28 — transfers live on this page too, shaped into the material
// request's format by asRequestRow() so nothing here learns a second schema.
import {
  fetchMyTransfers, asRequestRow, withdrawTransfer, transferWithdrawOffer,
} from '../../lib/branchTransfers';
// #590 -- taking a request back, both kinds, through one sheet.
import { withdrawMaterialRequest } from '../../lib/materialRequests';
import WithdrawSheet from './WithdrawSheet';
import { useTier } from '../../lib/tiers';
import useMessage from '../../lib/useMessage';
import InlineMessage from '../../components/InlineMessage';
// #517, Session 79.10 -- the partial-shipment caption. Imported rather than
// rewritten here: a second copy of this arithmetic is a second implementation
// and it can disagree with the one that has tests, which is #514's lesson.
import { shipmentStage, UNFILLED_STATUS, refusedByAsker } from '../../lib/transferLoads';
import { dateList } from '../../lib/format';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import { copyText } from '../../lib/clipboard';

// Issue #391, Session 78n. Gate: docs/gates/Stede_MyRequests_Gate_391.html,
// approved by Colby at 78n's open. Read it before changing anything here — the
// twelve decisions it records are why this page exists separately rather than
// as a tab on the Requests inbox, and the argument is not obvious from the code.
//
// The short version, because it is the thing most likely to be "simplified"
// away by a later session: RequestsInboxPage.js fetches
// `.in('status', ['pending','ordered'])`. A requester's single most important
// row is a DENIED one, and that fetch cannot return it. So a "Mine" tab there
// was never a filter over data already on the page — it is a second query with
// a different status set, wearing the first query's clothes. Once the query is
// separate, sharing the screen buys nothing and costs the framing: the inbox
// presents as an approval queue to a reader who cannot approve.
//
// D2 — this page is scoped to `requested_by = me` on EVERY role, not only for
// team members. A manager opening it sees what they raised, not their branch's
// queue. One screen, one question. A screen that means two different things
// depending on who opens it is exactly how #358's badge went wrong.
//
// D4 — read-only. No approve, deny, cancel or edit control anywhere, for any
// role. Cancel-my-own-request is a real feature and is deliberately out of
// scope: it is a write on a record other people are already acting on, and
// `cancelled` versus `denied` is a distinction this schema takes seriously.
//
// Callout I — no price anywhere, for any role. This is NOT a permission check.
// There is simply nothing here to hide: what a requester needs to know is
// whether it is coming. Cost belongs to purchasing's screens, and putting it
// here would mean maintaining a per-role gate on this page forever.

// ─── Constants ────────────────────────────────────────────────────────────────

const BRANCHES = [
  { id: 1, name: 'Dallas' },
  { id: 2, name: 'Houston' },
  { id: 3, name: 'Austin' },
  { id: 4, name: 'San Antonio' },
];

// D3 — every status, in two groups. `material_requests.status` is a real enum
// with TEN values (CLAUDE.md, data-layer gotchas); all ten are placed here
// deliberately rather than defaulted, because a status that falls through no
// group renders nowhere and the row silently vanishes from the requester's own
// page — the exact defect this page exists to fix, reintroduced by omission.
//
// `denied` sits in OPEN rather than FINISHED, and that is the gate's reading
// rather than a slip. A denial is an outcome a person has to do something
// about — re-raise it, or accept it and find another way — so it belongs where
// they will see it. `cancelled` is genuinely finished: they or someone else
// stopped it on purpose. Screen 1 puts the denied card first for the same
// reason.
// #514 / gate D28, Session 79.7 — `requested` and `in_transit` are TRANSFER
// statuses joining the list. `approved`, `denied`, `received` and `cancelled`
// are shared with material requests and already read correctly for both.
export const OPEN_STATUSES = ['pending', 'pending_purchase_approval', 'approved', 'ordered', 'open', 'in_progress', 'denied',
  // #528, Session 79.10 -- `awaiting_confirmation` is OPEN, and leaving it out
  // was caught by this file's own "a status in no group vanishes" test rather
  // than by anybody noticing. A transfer sitting at that stage would have
  // dropped off the requester's list entirely: still live, still coming, and
  // invisible to the one person waiting for it.
  'requested', 'awaiting_confirmation', 'in_transit'];
// `closed_short` is finished rather than open: the balance is never arriving,
// so there is nothing left for the requester to wait on.
export const FINISHED_STATUSES = ['received', 'closed', 'cancelled', 'closed_short', 'closed_unfilled'];

// D5 — the four-stage tracker, straight from SAP's process flow. "Pending"
// is a word about the record; "waiting on Mike since Thursday" is information a
// person can act on. The stage list is fixed at four for every request so the
// tracker reads the same way every time — a tracker whose shape changes per row
// is a diagram, not a progress bar.
const STAGES = ['Sent', 'Approval', 'Ordered', 'Received'];

// #514 / gate D28 — a transfer's four stages, and EXACTLY ONE WORD CHANGES.
//
// That is not a nicety, it is what made D28 possible at all. #391's D5 fixes
// the stage list at four for every row, "because a tracker whose shape changes
// per row is a diagram, not a progress bar" — so a document needing a fifth
// stage could not have lived on this page. A transfer needs Sent, Approval,
// Shipped, Received; the material request's third stage is Ordered because
// somebody buys it, and the transfer's is Shipped because a branch sends it.
//
// #517 will want to record a load arriving in more than one truck. It must go
// INSIDE Shipped rather than become a fifth stage, for this same reason.
export const TRANSFER_STAGES = ['Sent', 'Approval', 'Shipped', 'Received'];

// #580 part (a), gate 553 F2 -- a request for the branch's OWN SHELF keeps the
// four-stage shape (#391 D5) with its own four words, because it never ships:
// the shop confirms a date, makes it, and each run lands on the shelf.
export const OWN_SHELF_STAGES = ['Asked', 'Confirmed', 'Being made', 'On the shelf'];

export function stagesFor(req) {
  if (req?.__kind === 'transfer' && req.__own) return OWN_SHELF_STAGES;
  return req?.__kind === 'transfer' ? TRANSFER_STAGES : STAGES;
}

// How far an own-shelf request has got. Confirmed is reached at approval and
// Being made at the first signed-off run; the last run finishes it.
export function ownShelfReached(req) {
  if (req?.status === 'received') return 4;
  if (req?.status === 'approved' || req?.status === 'in_transit') return (Number(req.__qtyReady) || 0) > 0 ? 3 : 2;
  return 1;
}

// #638, Session 79.22b.1 -- HOW FAR A TERMINATED TRANSFER ACTUALLY GOT.
//
// `closed_unfilled` means the sending branch said nothing more is coming. How
// many stages that transfer REACHED is not a property of the status: if a truck
// went out before the branch gave up, Shipped genuinely happened; if nothing
// ever left the building, it did not. One number in STAGE_REACHED cannot answer
// both, and the number there answered only the first -- so a transfer where
// nothing was ever made drew Shipped as DONE and Received as WHERE IT IS NOW,
// which is pixel for pixel how a truck on the road is drawn. The label said
// NOT COMING and the strip said it was nearly here.
export function endedTransferReached(req) {
  return (Number(req?.__qtyShipped) || 0) > 0 ? 3 : 2;
}

// #638 -- AN ENDING IS NOT A DESTINATION, and this is the half the reached
// number cannot fix on its own. The stage sitting AT `reached` is drawn amber,
// which in this strip's vocabulary means "this is where it is now" -- measured
// against three other cards before the fix was designed. A document that has
// STOPPED has no such stage, so for these statuses nothing is amber and the
// unreached stages stay grey. The ending itself is carried by the label and the
// banner above, which already say what happened.
//
// `closed_short` is deliberately NOT here, and that settles the other ending
// this issue asked about: goods DID leave and some DID arrive, so all four
// stages genuinely happened. It sits at 4, every stage dark, nothing amber
// already -- and the card says what never turned up.
export const ENDED_EARLY = new Set([UNFILLED_STATUS]);

// #588 -- the promise names the branch that MADE it, read off the transfer. It
// was the literal "Houston promised" for anything made to order: true while
// Houston is the only maker, wrong the day another branch makes something.
export function promisedBy(req) {
  const name = BRANCHES.find(b => String(b.id) === String(req?.__fromBranchId))?.name;
  return name ? `${name} promised` : 'Promised';
}

// #618 / #637 -- who a refused item is NOT coming from, by name.
export function sendingBranchName(req) {
  return BRANCHES.find(b => String(b.id) === String(req?.__fromBranchId))?.name || 'The sending branch';
}

// #572, Session 79.22b.4.7.1 -- WHAT A REFUSED ITEM SAYS, BY WHOSE REFUSAL IT
// WAS. One the asking branch turned down itself ("Only some of these are
// needed", #698) is that branch's own decision, quoted - the words Colby
// approved at the open. It said "San Antonio is not sending this" about a
// branch that never saw the request. The sending branch's own refusal keeps
// today's words.
export function refusedItemSentence(req, it) {
  if (refusedByAsker(it?.__line, req?.__raw)) {
    const asker = BRANCHES.find(b => String(b.id) === String(req?.__toBranchId))?.name || 'Your branch';
    const why = String(it?.__line?.refusal_reason || '').trim();
    return `${asker} said this isn't needed${why ? `: “${why}”` : ''}`;
  }
  return `${sendingBranchName(req)} is not sending this`;
}

// ── #598 -- WHO A REQUEST IS WAITING ON, BY STAGE ───────────────────────────
//
// A transfer has two stages since #528, and they wait on different people:
// first the ASKER'S OWN approvers (their branch, their department), then the
// SUPPLYING branch. The row's `branch_id` is the supplying branch, so reading it
// at the first stage named the other branch's manager -- "With Paige Nolan"
// for a request that was waiting on Dallas. The same people the bells reach
// (#583) and the Withdraw sheet names. A purchase request is unchanged.
export function waitingOn(req) {
  if (req?.__kind !== 'transfer') return req;
  if (req.status === 'requested') {
    return { branch_id: req.__toBranchId, department_id: req.__raw?.requested_by_department_id ?? null };
  }
  if (req.status === 'awaiting_confirmation') return { branch_id: req.__fromBranchId, department_id: null };
  return req;
}

// ── #590 -- WHAT THE WITHDRAW CONTROL OFFERS, AND IN WHAT WORDS ────────────
//
// Pure, so the rules the gates set can be tested without a screen: gate 371
// D31-D37 for a transfer, gate 391 D13-D19 for a purchase request, and F2's
// strings for both. Returns null when nothing is offered -- the supplying
// branch, an ended request, everything already sent (F1: an absent control is
// honest where a dead one is not) -- or `{ orderedLine: true }` for a purchase
// request Purchasing has already ordered (D14).
const BRANCH_NAMES_BY_ID = { 1: 'Dallas', 2: 'Houston', 3: 'Austin', 4: 'San Antonio' };

export function withdrawOfferFor(req, { profile = null, approverName = null } = {}) {
  if (!req || !profile) return null;
  if (req.__kind === 'transfer') {
    // One copy of the transfer wording, shared with the transfer's detail.
    return transferWithdrawOffer(req.__raw, { profile, approverName, branchNames: BRANCH_NAMES_BY_ID });
  }
  // A purchase request -- gate 391 D13/D14: only the person who asked, only
  // until Purchasing orders it.
  if (!canWithdrawMaterialRequest(profile, req)) {
    const mine = String(req.requested_by) === String(profile.id);
    return mine && req.status === 'ordered' ? { orderedLine: true } : null;
  }
  const who = req.status === 'pending'
    ? (approverName || `the ${BRANCH_NAMES_BY_ID[req.branch_id] || 'branch'} managers`)
    : req.status === 'pending_purchase_approval' ? 'Purchasing and the executives' : 'Purchasing';
  const consequence = req.status === 'pending_purchase_approval'
    ? 'Purchasing and the executives will be told. Nothing has been ordered.'
    : `${who} will be told and it comes off their list. Nothing has been ordered.`;
  return {
    scope: 'whole', control: 'Withdraw this request', title: 'Withdraw this request?',
    subtitle: [req.request_number, requestTitle(req)].filter(Boolean).join(' \u00b7 '),
    // #643 -- "so the Dallas managers KNOWS" and "so Purchasing and the
    // executives KNOWS" were both live before this. See knowsVerb().
    consequence, who, reasonLabel: `Why? ${who} will see this.`,
    blockedReason: `Say why, so ${who} ${knowsVerb(who)}.`,
    keepLabel: 'Keep it', submitLabel: 'Withdraw request',
  };
}

// ── #590 / #592 -- HOW AN ENDED REQUEST READS ───────────────────────────────
//
// Withdrawn or canceled is decided by WHO ended it (gate 391 B6): the person
// who asked withdrew it; anybody else canceled it -- Purchasing, for a purchase
// request. A transfer the asking side took back is always Withdrawn (371 D32).
// American spelling in every visible word: "Canceled".
// The two sentences 20260919_transfer_request_fixes_79_21b.sql wrote into rows
// that ended before reasons were kept -- statement 9 for purchase requests,
// statement 4 for transfers. Copied from the migration, never retyped by hand.
const LEGACY_ENDINGS = ['Recorded before reasons were kept', 'Recorded before withdrawing existed'];

export function endedPresentation(req, names = {}) {
  if (!req || req.status !== 'cancelled') return null;
  const day = req.cancelled_at ? fmtDayName(req.cancelled_at) : null;
  const reason = req.cancel_reason || null;
  // Gate 391 P9 / 371 D34 -- A REQUEST ENDED BEFORE THIS BUILD. The migration
  // gave each one a sentence, because the database now refuses an ending with
  // no reason. Nobody said it, so it is not quoted, and nobody is named: who
  // ended it was never recorded.
  if (!req.cancelled_by && LEGACY_ENDINGS.includes(reason)) {
    return {
      pill: req.__kind === 'transfer' ? 'Withdrawn' : 'Canceled',
      tone: req.__kind === 'transfer' ? 'neutral' : 'denied',
      reason: null, note: `${reason}.`, line: null,
    };
  }
  if (req.__kind === 'transfer') {
    const by = req.cancelled_by ? names[req.cancelled_by] : null;
    return {
      pill: 'Withdrawn', tone: 'neutral', reason,
      line: [`Withdrawn${day ? ` ${day}` : ''}`, by ? `by ${by}` : null].filter(Boolean).join(' '),
    };
  }
  const self = req.cancelled_by && String(req.cancelled_by) === String(req.requested_by);
  if (self) {
    return { pill: 'Withdrawn', tone: 'neutral', reason, line: `You withdrew this${day ? ` ${day}` : ''}` };
  }
  const by = req.cancelled_by ? names[req.cancelled_by] : null;
  return {
    pill: 'Canceled', tone: 'denied', reason,
    // "“{reason}” — {name}, Purchasing, {day}" (gate 391 F2), the reason
    // itself being the banner's quoted line.
    line: [by ? `${by}, Purchasing` : 'Purchasing', day].filter(Boolean).join(', '),
  };
}

// #614, gate 371 D40 -- A PURCHASE REQUEST SENT AS A TRANSFER. Purchasing sent
// it to one of our own branches, so it is closed - and the closed words are
// "Received", with a tracker at its last stage, which would tell the person
// who asked that their material had arrived when it had only just been sent.
// It reads as what happened instead; the transfer, which is theirs too, is in
// the same list and carries the progress.
export function sentAsTransferOf(req) {
  if (!req || req.__kind === 'transfer' || req.status !== 'closed') return null;
  return (Array.isArray(req.sent_as) && req.sent_as[0]) || null;
}

// The pill, in the same words. Other statuses keep their presentation.
export function ownShelfLabel(req) {
  if (req?.status === 'received') return 'On the shelf';
  if (req?.status === 'approved') return 'Being made';
  return null;
}

// How far along each status is: the index of the first stage NOT yet reached.
// `denied` is absent on purpose — it collapses the tracker entirely (D5) and is
// handled before this map is consulted.
export const STAGE_REACHED = {
  open: 1,
  in_progress: 1,
  pending: 1,
  pending_purchase_approval: 1,
  approved: 2,
  ordered: 3,
  received: 4,
  closed: 4,
  cancelled: 1,
  // #528, Session 79.10 -- the asking branch's own approval. It sits at stage 1
  // alongside a material request's `pending`, because it is the same question
  // asked of the same people: may this person ask for this?
  awaiting_confirmation: 1,
  // #514 / D28 — the transfer statuses. `approved` and `received` are shared
  // and already correct for both documents: a transfer that is approved sits at
  // Shipped exactly as a material request sits at Ordered, which is why one
  // number serves both and no per-kind lookup is needed here.
  requested: 1,
  in_transit: 3,
  // A short close ends the document. The tracker shows Received as reached
  // because the movement is over, and the card says what did not arrive.
  closed_short: 4,
  // #517 / D56 -- THE SECOND ENDING, and it stops one stage short of the first
  // on purpose. A short close means goods LEFT and vanished, so the shipping
  // half genuinely happened. `closed_unfilled` means a balance that never left
  // the building.
  //
  // ** THIS NUMBER IS NOT THE WHOLE ANSWER AND MUST NOT BE READ AS ONE -- #638,
  // Session 79.22b.1. ** It is right only when something shipped before the
  // branch gave up. Where NOTHING ever left, 3 lit Shipped as DONE for a truck
  // that never existed. The card asks `endedTransferReached(req)` instead,
  // which reads the quantity; this stays as the shipped-something answer and as
  // the fallback for any caller with no row to ask. `ENDED_EARLY` separately
  // stops the next stage being drawn as "where it is now".
  closed_unfilled: 3,
};

export const STATUS_PRESENTATION = {
  pending:                  { label: 'Waiting',    color: '#EF9F27' },
  pending_purchase_approval:{ label: 'Waiting',    color: '#EF9F27' },
  open:                     { label: 'Waiting',    color: '#EF9F27' },
  in_progress:              { label: 'Waiting',    color: '#EF9F27' },
  approved:                 { label: 'Approved',   color: '#1D9E75' },
  ordered:                  { label: 'On the way', color: '#378ADD' },
  received:                 { label: 'Received',   color: '#1D9E75' },
  closed:                   { label: 'Received',   color: '#1D9E75' },
  denied:                   { label: 'Denied',     color: '#E24B4A' },
  cancelled:                { label: 'Canceled',   color: '#5C5C5C' },
  // #514 / D28 — transfer statuses, worded as the REQUESTER experiences them
  // rather than as the document names them. "Requested" is a word about the
  // record; "Waiting" is what it means to the person who is waiting.
  requested:                { label: 'Waiting',      color: '#EF9F27' },
  in_transit:               { label: 'On the way',   color: '#378ADD' },
  closed_short:             { label: 'Closed short', color: '#A32D2D' },
  // #528 -- to the person who asked, both approval stages are simply "waiting".
  // Naming the stage here would tell a requester which manager to chase, which
  // is not their job and is the noise #391 exists to remove.
  awaiting_confirmation:    { label: 'Waiting',      color: '#EF9F27' },
  // #517 / D56 -- NOT RED, and that is the decision rather than a colour
  // choice. Red is what the app uses for something going wrong, and nothing
  // went wrong here: no money was lost and nothing is missing. The balance is
  // simply not coming, which is news the requester must act on rather than an
  // alarm. Amber is the app's "you need to know this" colour.
  closed_unfilled:          { label: 'Not coming',   color: '#EF9F27' },
};

const INVENTORY_GREEN = '#1D9E75';

// ─── Helpers (all hoisted — locked rule 2 forbids defining these inside a
//     component body, which would remount them on every render) ──────────────

function branchName(id) {
  return BRANCHES.find(b => b.id === id)?.name || '—';
}

// #544 as amended at 79.21b -- THE SHARED dateList(), which reads a bare DATE
// at local noon. This page's own copy read one as UTC midnight and showed every
// promised and needed date a day early: "Houston promised Sep 24" for a promise
// of Sep 25. Same "Sep 25" shape; only the day was wrong.
function fmtDate(ts) {
  return dateList(ts);
}

function fmtDayName(ts) {
  if (!ts) return null;
  const then = new Date(ts);
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  // Inside a week a weekday name is what a person actually remembers —
  // "since Thursday" beats "since Aug 27" for the thing this line is for,
  // which is deciding whether to go and ask. Past a week the name stops being
  // unambiguous and the date is more use.
  if (days <= 6) return then.toLocaleDateString('en-US', { weekday: 'long' });
  return fmtDate(ts);
}

function presentation(status) {
  return STATUS_PRESENTATION[status] || { label: status || '—', color: '#5C5C5C' };
}

function requestTitle(req) {
  const items = req.material_request_items || [];
  if (!items.length) return req.request_number;
  return itemLabel(items[0]);
}

function requestSubtitle(req) {
  const items = req.material_request_items || [];
  if (!items.length) return '';
  const first = items[0];
  const qty = first.qty_requested;
  const uom = itemUom(first);
  const qtyText = qty != null ? `qty ${qty}${uom ? ' ' + uom : ''}` : '';
  const extra = items.length > 1 ? ` · +${items.length - 1} more` : '';
  return `${qtyText}${extra}`;
}

// D6 — carrier, number and ETA, shown to the requester. Stede already stores
// all three; until this page they were read only by staff screens. Per-item
// tracking is the real location (trackingSummary), with the request-level
// tracking_eta surviving as the fallback Mark Ordered writes before any real
// carrier number is entered.
function trackingFor(req) {
  const items = req.material_request_items || [];
  const summary = trackingSummary(items);
  const withNumber = items.find(it => it.tracking_number);
  return {
    carrier: withNumber?.tracking_carrier || req.tracking_carrier || null,
    number: withNumber?.tracking_number || req.tracking_number || null,
    eta: summary.earliestEta || req.tracking_eta || null,
  };
}

function isOpenStatus(status) {
  return OPEN_STATUSES.includes(status);
}

// Denied first, then newest first. Callout A: the person who has been told no
// needs to see it before anything else on the page.
function sortOpen(a, b) {
  const aDenied = a.status === 'denied';
  const bDenied = b.status === 'denied';
  if (aDenied !== bDenied) return aDenied ? -1 : 1;
  return new Date(b.created_at) - new Date(a.created_at);
}

function sortFinished(a, b) {
  return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatusPill({ status, label: labelOverride = null }) {
  const { label: base, color } = presentation(status);
  const label = labelOverride || base;
  return (
    <span style={{
      display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
      textTransform: 'uppercase', color, background: color + '18',
      border: `1px solid ${color}44`, borderRadius: 20, padding: '3px 10px',
      whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

// D7 — the denial reason, in full, at the top of the card.
//
// TWO fields, not one, and getting this wrong tells the requester something
// false. Session 78n5 found this screen still reading only the note.
//
//   reasonLabel — the governed reason the manager picked, frozen at the moment
//                 of denial (`denial_reason_label`). REQUIRED on the Deny sheet.
//   note        — the manager's free-text elaboration (`denial_reason`).
//                 OPTIONAL, and usually absent.
//
// Because the note is optional and the reason is not, reading only the note
// meant the ordinary denial — a reason picked, no note typed — rendered as
// "No reason was recorded." That is not merely incomplete, it is wrong: a
// reason WAS recorded, the requester's notification quotes it, and this screen
// is the permanent record that contradicted it. Exactly the dead end #397
// exists to close, restated by the screen meant to close it.
//
// "No reason was recorded" is now reachable only when BOTH are absent, which is
// the genuine legacy case — a denial made before #397 shipped. Those exist on
// real data and must keep reading honestly.
//
// Superseded note, kept because it dates the gap: 78n recorded that
// `denial_reason` was written ONLY by the executive's over-threshold denial in
// PurchasingQueuePage.js, and that the manager's deny captured nothing at all.
// #397 fixed the capture at 78n4 and did not follow it to this render.
// #590 / #592 -- the same banner carries a withdrawal and a cancellation
// (gate 391 F5: "rather than a look-alike beside it"). `endedLine` replaces
// "Denied {when}"; `tone` is neutral for a request the asker took back
// themselves, because nothing went wrong.
function DeniedBanner({ reasonLabel, note, whenLabel, endedLine = null, tone = 'denied', missingNote = null }) {
  const hasAny = !!reasonLabel || !!note;
  const neutral = tone === 'neutral';
  return (
    <div style={{
      background: neutral ? '#F4F6F8' : '#FDF2F2',
      border: neutral ? '1px solid #DCE2E8' : '1px solid #E24B4A55',
      borderLeft: neutral ? '3px solid #8A97A3' : '3px solid #E24B4A',
      borderRadius: 6, padding: '10px 12px', marginBottom: 10,
    }}>
      {reasonLabel && (
        <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.45, color: '#2A2A2A' }}>
          {reasonLabel}
        </div>
      )}
      {note && (
        <div style={{
          fontSize: 14, lineHeight: 1.45, color: '#2A2A2A',
          marginTop: reasonLabel ? 4 : 0,
        }}>
          &ldquo;{note}&rdquo;
        </div>
      )}
      {!hasAny && (
        <div style={{ fontSize: 14, lineHeight: 1.45, color: neutral ? '#48545F' : '#6A4A4A', fontStyle: 'italic' }}>
          {missingNote || 'No reason was recorded.'}
        </div>
      )}
      {endedLine ? (
        <div style={{ fontSize: 12, color: neutral ? '#48545F' : '#8A3A3A', marginTop: 4 }}>{endedLine}</div>
      ) : whenLabel && (
        <div style={{ fontSize: 12, color: '#8A3A3A', marginTop: 4 }}>Denied {whenLabel}</div>
      )}
    </div>
  );
}

// `stages` defaults to the material-request list so every existing caller and
// every existing test keeps its exact behaviour — D28 adds a second word set,
// it does not change the first.
function StageTracker({ status, stages = STAGES, reached: reachedOverride = null }) {
  const reached = reachedOverride ?? STAGE_REACHED[status] ?? 1;
  // #638 -- see ENDED_EARLY. A stopped document has no stage it is "at".
  const over = ENDED_EARLY.has(status);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, margin: '10px 0 8px' }}>
      {stages.map((stage, i) => {
        const done = i < reached;
        const isNext = !over && i === reached;
        const color = done ? INVENTORY_GREEN : isNext ? '#EF9F27' : '#D8D8D8';
        return (
          <React.Fragment key={stage}>
            {i > 0 && (
              <span style={{
                flex: 1, height: 2, background: done ? INVENTORY_GREEN : '#E4E4E4',
              }} />
            )}
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 62 }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%', background: color,
                border: isNext ? `2px solid ${color}` : 'none',
                boxShadow: isNext ? `0 0 0 3px ${color}25` : 'none',
              }} />
              <span style={{
                fontSize: 10, marginTop: 5, letterSpacing: '0.03em',
                color: done ? '#2A2A2A' : isNext ? '#8A6415' : '#A8A8A8',
                fontWeight: done || isNext ? 600 : 400,
              }}>{stage}</span>
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// D5's second half — who is holding it, and since when.
//
// NAMES A BRANCH, NOT A PERSON, and that is a deliberate retreat from what the
// gate drew. The gate shows "With Mike R. since Thursday", and the first build
// of this delivered it by looking up the branch's managers and printing the
// first one, which produced "With Austin Martinez +3".
//
// Colby caught it immediately and the objection is structural, not cosmetic:
// canApprove() (src/lib/canApprove.js) lets ANY manager at the branch approve
// ANY request there, so Dallas has four people who can approve and no notion of
// which one owns it. Austin Martinez is the Production Manager; Dayton Harkey
// is the Branch Manager; Ricky Allen holds `manager` only because the Tools
// module requires it. Naming the first row the database returned was not merely
// arbitrary, it asserted an ownership Stede does not model.
//
// So this states exactly what is true today — the request is with that branch's
// managers — and nothing more. #396 is the issue that makes a named approver
// truthful: approval as a per-user capability plus routing by department. When
// it ships, this line gets its name back and the gate's wording becomes
// deliverable rather than aspirational.
//
// The manager lookup that fed the old version was removed with it rather than
// left running: a query whose result nothing renders is a cost with no reader.
// RESTORED at Session 78n4, #396. The retreat above is superseded and kept
// because it explains why the name went away — but the condition it named has
// now been met. Approval is a per-person capability and requests route by the
// requester's department, so exactly two people can act on any request and the
// name is TRUE rather than the first row a query happened to return.
//
// Which name: the DEPARTMENT manager when there is one, because that is who the
// request is routed to. The branch manager is the backstop who can also act and
// is named only when there is no departmental owner — which is the honest read
// of a request from somebody with no department set.
//
// It still degrades to the branch wording, and that path is not dead code:
// a department with no manager assigned, or a manager whose approval switch is
// off, leaves the branch manager alone in the set. Naming them is right. Naming
// NOBODY — an empty set — is possible too, and is exactly the silent failure
// D5 exists to prevent, so it renders as a plain statement rather than blank.
function WaitingLine({ req, managers }) {
  const since = fmtDayName(req.created_at);
  // #598 -- the people it is ACTUALLY waiting on, at this stage.
  const target = waitingOn(req);
  // #643 -- EVERYBODY WHO CAN ACT, joined with "or" because either of them can.
  // Naming only the first was not merely incomplete: which one it named moved
  // when an unrelated profile was edited, and singling out one of two equally
  // responsible colleagues reads as an accusation.
  const owners = approverPhrase(topApprovers(managers, target), { join: 'or' });

  return (
    <div style={{ fontSize: 13, color: '#6A6A6A' }}>
      {owners ? (
        <>
          With <strong style={{ color: '#2A2A2A' }}>{owners}</strong>
          {since ? ` since ${since}` : ''}
        </>
      ) : (
        <>
          With the <strong style={{ color: '#2A2A2A' }}>{branchName(target.branch_id)}</strong> managers
          {since ? ` since ${since}` : ''}
        </>
      )}
    </div>
  );
}

// Shown once a request has actually been approved. Deliberately renders nothing
// when the name is missing: `approved_by` was only written from Session 78n
// onward, so every older request has none, and "Approved by —" would read as a
// broken field rather than as absent history.
function ApprovedByLine({ req, approvedByNames }) {
  const name = approvedByNames[req.approved_by];
  if (!name) return null;
  const when = req.approved_at ? fmtDayName(req.approved_at) : null;
  return (
    <div style={{ fontSize: 13, color: '#6A6A6A' }}>
      Approved by <strong style={{ color: '#2A2A2A' }}>{name}</strong>
      {when ? ` ${when}` : ''}
    </div>
  );
}

// #413, Session 78n8.3. The copy control.
//
// WHY IT EXISTS: a tracking number is only ever wanted somewhere else — pasted
// into the carrier's site — and a PO number is what a requester quotes when they
// ring the vendor or ask purchasing about their order. Both are long strings of
// characters that are miserable to retype from a phone screen and easy to get
// subtly wrong, which produces a "not found" that looks like the order is lost.
//
// stopPropagation IS LOAD-BEARING, not defensive. The whole card carries an
// onClick that selects and expands it, so without this a tap on Copy would also
// toggle the card open or shut underneath the reader's thumb — the copy would
// work and would look like a mis-tap.
//
// Defined at module level, per locked rule 2: a component declared inside
// another component's body remounts on every render.
function CopyButton({ value, title }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;

  function handleCopy(e) {
    e.stopPropagation();
    copyText(value).then(ok => {
      // No tick when the clipboard genuinely refused. A tick that appears
      // whether or not the copy happened is worse than none: the reader pastes
      // whatever was on the clipboard before and blames the vendor's website.
      if (!ok) return;
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={title}
      aria-label={title}
      style={{
        border: '1px solid #E0E0E0', background: copied ? '#E1F5EE' : '#FFFFFF',
        borderRadius: 6, width: 24, height: 24, display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        padding: 0, flexShrink: 0,
      }}
    >
      {copied ? <IconCheck size={12} color="#085041" /> : <IconCopy size={12} color="#6A6A6A" />}
    </button>
  );
}

function TrackingLine({ req }) {
  const { carrier, number, eta } = trackingFor(req);
  const poRef = req.po_ref;
  if (!carrier && !number && !eta && !poRef) return null;
  return (
    <div>
      {/* #413 — the PO number, which this card has never shown. Colby's ask, and
          it goes ON THE CARD rather than behind a detail sheet. He initially
          asked for a mobile detail sheet; this page deliberately has none, on
          the argument the #391 gate makes against SAP — everything a person asks
          about is on the card, "no detail panel, no navigation, nothing to come
          back from". Shown that, he ruled for the card. Zero taps, not one. */}
      {poRef && (
        <div style={{ fontSize: 13, color: '#6A6A6A', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span>PO</span>
          <span style={{ fontVariantNumeric: 'tabular-nums', color: '#2A2A2A', fontWeight: 600 }}>{poRef}</span>
          <CopyButton value={poRef} title="Copy PO number" />
        </div>
      )}
      {(carrier || number || eta) && (
        <div style={{ fontSize: 13, color: '#6A6A6A', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {carrier && <strong style={{ color: '#2A2A2A' }}>{carrier}</strong>}
          {number && <span style={{ fontVariantNumeric: 'tabular-nums' }}>{number}</span>}
          <CopyButton value={number} title="Copy tracking number" />
          {eta && <span>· due {fmtDate(eta)}</span>}
        </div>
      )}
    </div>
  );
}

// Callout H — what was asked for, in the requester's own words. Multi-item
// requests list every line. The note typed at 8am is shown back to them,
// because it is what makes a two-week-old request recognisable.
export function AskedForBlock({ req }) {
  const items = req.material_request_items || [];
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{
        fontFamily: 'var(--font-head)', fontSize: 11, fontWeight: 700,
        letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8A8A8A', marginBottom: 6,
      }}>What you asked for</div>
      {items.map(it => {
        // #618 / #637, Session 79.22b -- EACH ITEM'S OWN PROMISE, beside the
        // item, the way Houston entered it (Colby's ruling). Transfers only:
        // this list is shared with material requests, which carry no such date.
        // A refused item says it is not coming rather than showing a date.
        const isTransfer = req.__kind === 'transfer';
        const itemLate = isTransfer && !!it.__promisedDate && !!req.__neededBy
          && it.__promisedDate > req.__neededBy;
        return (
          <div key={it.id} style={{
            display: 'flex', justifyContent: 'space-between', gap: 12,
            padding: '6px 0', borderBottom: '1px solid #F0F0F0', fontSize: 14,
          }}>
            <span style={{ color: '#2A2A2A' }}>
              {itemLabel(it)}
              {isTransfer && it.__refused && (
                <span style={{ display: 'block', fontSize: 12, color: '#8A3A0F', marginTop: 2 }}>
                  {refusedItemSentence(req, it)}
                </span>
              )}
              {isTransfer && !it.__refused && it.__promisedDate && (
                <span style={{ display: 'block', fontSize: 12, color: itemLate ? '#8A3A0F' : '#5C5C5C', marginTop: 2 }}>
                  Promised <strong>{fmtDate(it.__promisedDate)}</strong>
                  {itemLate ? ' — later than you asked' : ''}
                </span>
              )}
            </span>
            <span style={{ color: '#6A6A6A', whiteSpace: 'nowrap' }}>
              {it.qty_requested}{itemUom(it) ? ' ' + itemUom(it) : ''}
            </span>
          </div>
        );
      })}
      {req.notes && (
        <div style={{ fontSize: 13, color: '#6A6A6A', fontStyle: 'italic', marginTop: 8 }}>
          &ldquo;{req.notes}&rdquo;
        </div>
      )}
    </div>
  );
}

// #514 / gate D28 — THE ONLY GENUINELY NEW WRITING THIS DECISION NEEDED.
//
// Everything else a transfer shows on this page already existed for material
// requests: the tracker, the waiting line, the denial banner, the asked-for
// block. Two facts have no material-request equivalent, and both are the
// requester's real questions:
//
//   * WHAT THE MAKER PROMISED against what was asked. Houston commits to a date
//     when it accepts, and until this line the requester was told that date
//     existed and never shown it. A promise later than the need is the thing
//     they most need to see, so it is called out rather than merely printed.
//   * HOW MANY ACTUALLY SHIPPED. Short-sending is allowed, so "150 asked, 120
//     sent" is a normal outcome that the status word alone cannot express.
//
// Deliberately NOT a second tracker and not a second screen: #391 exists
// because "Mine" was a tab on the Requests inbox running a query that could not
// return a denied row, and a separate transfer-tracking page would recreate it.
export function TransferLines({ req }) {
  const promised = req.__promisedDate;
  // #618 / #637, Session 79.22b -- THE FIRST LINE A PERSON READS MUST BE TRUE ON
  // ITS OWN. Houston may give each item its own date (gate 517 D67), and this
  // printed the transfer's date alone - "Houston promised Oct 14" above frames
  // promised for the 20th. It now names the span, and how many items land after
  // the date asked for; each item's own date is on the list below.
  const last = req.__promisedLast && req.__promisedLast !== promised ? req.__promisedLast : null;
  const needed = req.__neededBy;
  const lateItems = Number(req.__lateItems) || 0;
  const late = lateItems > 0
    || (!(req.material_request_items || []).length && !!promised && !!needed && promised > needed);
  // #517 / D52 -- THE GAP IS SHOWN INSIDE THE SHIPPED STAGE, not as a stage of
  // its own, because #391's D5 fixes this tracker at four stages for every row
  // and a transfer needing a fifth could not share this page at all.
  //
  // THE SENTENCE THIS REPLACES WAS "the rest was not sent", AND IT IS NOW THE
  // OPPOSITE OF THE RULING. Colby approved D51: after a partial despatch the
  // balance STAYS OWED and the request stays open. "The rest was not sent"
  // reads as final -- it tells somebody still waiting for 150 frames to stop
  // waiting.
  const stage = shipmentStage({ status: req.status }, req.material_request_items || []);
  const asked = req.__qtyAsked;
  const shipped = req.__qtyShipped;
  // An own-shelf request never ships, so it never says "sent" (553 D21).
  const shownShipped = !req.__own && ['in_transit', 'received', 'closed_short', UNFILLED_STATUS]
    .includes(req.status);
  const made = Number(req.__qtyReady) || 0;
  const shownMade = req.__own && ['approved', 'received'].includes(req.status) && made > 0;

  if (!promised && !shownShipped && !shownMade) return null;
  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
      {promised && (
        <div style={{ fontSize: 13, color: late ? '#8A3A0F' : '#5C5C5C' }}>
          {/* #588 -- THE BRANCH THAT MADE THE PROMISE, not "Houston" for
              anything made to order. A made item can be promised by any maker,
              and a shelf transfer is promised by whoever holds the shelf. */}
          {promisedBy(req)} <strong>{fmtDate(promised)}</strong>
          {last ? <> to <strong>{fmtDate(last)}</strong></> : null}
          {needed ? ` \u00b7 you asked for ${fmtDate(needed)}` : ''}
          {late
            ? (last
              ? ` \u2014 ${lateItems} ${lateItems === 1 ? 'item' : 'items'} later than you asked`
              : ' \u2014 later than you asked')
            : ''}
        </div>
      )}
      {shownShipped && (
        <div style={{ fontSize: 13, color: stage.stopped ? '#8A3A0F' : '#5C5C5C' }}>
          {stage.caption || <><strong>{shipped}</strong> of {asked} sent</>}
        </div>
      )}
      {shownMade && (
        <div style={{ fontSize: 13, color: '#5C5C5C' }}>
          <strong>{made}</strong> of {asked} made and on the shelf
        </div>
      )}
    </div>
  );
}

// One card. Callout F — no buttons anywhere on it. Every control this screen
// could offer would be one the reader is not allowed to press, which is the
// exact defect being fixed.
// #597 and #590 -- EVERYTHING BELOW THE HEADER, ONCE. The phone's card and the
// desk's detail panel both render this, because the panel was written for
// material requests and never learned about transfers: it drew "Ordered" on a
// transfer and none of its promise or progress lines, while the card beside it
// was right. Two copies of one screen drift in exactly one half.
function RequestStatusBlock({ req, managers = [], approvedByNames = {} }) {
  const denied = req.status === 'denied';
  const ended = endedPresentation(req, approvedByNames);
  const sentAs = sentAsTransferOf(req);   // #614
  const showTracker = !denied && req.status !== 'cancelled' && !sentAs;
  return (
    <>
      {denied && (
        <div style={{ marginTop: 10 }}>
          <DeniedBanner reasonLabel={req.denial_reason_label} note={req.denial_reason} whenLabel={fmtDayName(req.updated_at)} />
        </div>
      )}
      {ended && (
        <div style={{ marginTop: 10 }}>
          <DeniedBanner note={ended.reason} endedLine={ended.line} tone={ended.tone} missingNote={ended.note || null} />
        </div>
      )}

      {showTracker && (
        <StageTracker status={req.status} stages={stagesFor(req)}
          reached={req.__own ? ownShelfReached(req)
            // #638 -- whether Shipped was reached depends on whether anything
            // ever left, which the status alone cannot say.
            : req.status === UNFILLED_STATUS ? endedTransferReached(req)
              : null} />
      )}

      {req.__kind === 'transfer' && <TransferLines req={req} />}

      {(req.status === 'pending' || req.status === 'pending_purchase_approval' || req.status === 'open' || req.status === 'in_progress'
        // #514 / D28 — a transfer waiting on the SENDING branch's managers. The
        // waiting line names a branch rather than a person by design (see its
        // own comment), and `branch_id` is set to the branch being asked, so it
        // needs no transfer-specific branch here.
        || req.status === 'requested' || req.status === 'awaiting_confirmation') && (
        <WaitingLine req={req} managers={managers} />
      )}
      {req.status === 'approved' && <ApprovedByLine req={req} approvedByNames={approvedByNames} />}
      {req.status === 'ordered' && <TrackingLine req={req} />}
      {(req.status === 'received' || req.status === 'closed') && !sentAs && (
        <div style={{ fontSize: 13, color: '#5C5C5C' }}>
          {req.__own ? 'On the shelf' : 'Received'} {fmtDate(req.updated_at || req.created_at)}
        </div>
      )}
      {sentAs && (
        <div style={{ fontSize: 13, color: '#5C5C5C', marginTop: 8, lineHeight: 1.45 }}>
          Purchasing sent this to {branchName(sentAs.from_branch_id)} as a transfer {fmtDate(req.updated_at || req.created_at)}.
          {' '}Nothing is bought — follow it in the transfer in this list.
        </div>
      )}
      {req.status === 'closed_short' && (
        <div style={{ fontSize: 13, color: '#8A3A0F' }}>
          Closed {fmtDate(req.updated_at || req.created_at)} with material outstanding
          {req.denial_reason ? ` — ${req.denial_reason}` : ''}
        </div>
      )}
      {/* 371 D33 -- the asking branch stopped the rest: its reason, in its words. */}
      {req.status === 'closed_unfilled' && req.unfilled_reason && (
        <div style={{ fontSize: 13, color: '#5C5C5C', marginTop: 4 }}>
          &ldquo;{req.unfilled_reason}&rdquo;
        </div>
      )}
    </>
  );
}

// 371 D37 / 391 D19 -- ONE action, AT THE FOOT of the opened request: after
// what was asked for, on the phone's card and on the desk's panel alike. It
// first shipped inside the status block, above "What you asked for", which is
// not where either gate draws it (F1: "at the foot of the opened card").
function WithdrawFoot({ req, offer = null, onWithdraw = null }) {
  if (!offer) return null;
  return (
    <>
      {/* gate 391 D14 -- once Purchasing has ordered it there is no button, and
          the card says who can stop it. */}
      {offer.orderedLine && (
        <div style={{ fontSize: 13, color: '#3A3A3A', marginTop: 10 }}>
          Ordered — if you no longer need it, ask Purchasing to cancel it.
        </div>
      )}
      {/* stopPropagation: the card itself selects on a desk. */}
      {offer.control && onWithdraw && (
        <button
          onClick={e => { e.stopPropagation(); onWithdraw(req, offer); }}
          className="btn mr-focus"
          style={{
            marginTop: 12, minHeight: 40, justifyContent: 'center',
            background: '#FFFFFF', color: '#A32D2D', border: '1px solid #E3B8B8',
          }}>
          {offer.control}
        </button>
      )}
    </>
  );
}

// Exported for its keyboard and ring tests (#679, #744).
export function RequestCard({
  req, managers = [], approvedByNames = {}, expanded, onSelect, onKeySelect = null, selected, flash = false,
  offer = null, onWithdraw = null,
}) {
  const ended = endedPresentation(req, approvedByNames);
  return (
    <div
      // #744 -- the anchor a notification's link scrolls to on a phone.
      id={`request-card-${req.id}`}
      onClick={onSelect}
      // #679, Session 79.22b.6d -- A KEYBOARD CAN REACH AND OPEN THE CARD. On a
      // desk the card was a plain clickable box: Tab skipped every one, and the
      // only way to the panel holding Withdraw was a mouse. Enter or Space opens
      // it as a click does, and moves the keyboard into the panel (the page's
      // onKeySelect). Only where the card selects -- the phone's cards select
      // nothing and are left as they are.
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      aria-pressed={onSelect ? !!selected : undefined}
      className={onSelect ? 'mr-card' : undefined}
      onKeyDown={onSelect ? (e => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          (onKeySelect || onSelect)();
        }
      }) : undefined}
      style={{
        background: '#FFFFFF',
        border: `1px solid ${selected ? INVENTORY_GREEN : '#E4E4E4'}`,
        borderRadius: 10, padding: 14, marginBottom: 10,
        cursor: onSelect ? 'pointer' : 'default',
        boxShadow: selected ? `0 0 0 2px ${INVENTORY_GREEN}22` : 'none',
        // #744, the approved drawing -- the ring that says WHICH request the
        // bell named: the same 2px Inventory-green ring the Requests inbox
        // puts on a transfer its bell names (#605).
        ...(flash ? { outline: `2px solid ${INVENTORY_GREEN}`, outlineOffset: 2 } : null),
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontFamily: 'var(--font-head)', fontSize: 12, fontWeight: 700,
            letterSpacing: '0.06em', color: '#5C5C5C',
          }}>{req.request_number}</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A', marginTop: 2 }}>
            {requestTitle(req)}
          </div>
          <div style={{ fontSize: 13, color: '#5C5C5C', marginTop: 1 }}>
            {requestSubtitle(req)}
            {req.work_order_ref ? ` · WO# ${req.work_order_ref}` : ''}
          </div>
        </div>
        <StatusPill status={req.status} label={ended ? ended.pill
          : sentAsTransferOf(req) ? 'Sent as a transfer'
          : (req.__own ? ownShelfLabel(req) : null)} />
      </div>

      <RequestStatusBlock req={req} managers={managers} approvedByNames={approvedByNames} />

      {expanded && <AskedForBlock req={req} />}
      {/* The collapsed desk list carries no action (371 D37): the control is
          only drawn where the request is opened. */}
      {expanded && <WithdrawFoot req={req} offer={offer} onWithdraw={onWithdraw} />}
    </div>
  );
}

function GroupHeading({ children, count }) {
  return (
    <div style={{
      fontFamily: 'var(--font-head)', fontSize: 12, fontWeight: 700,
      letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8A8A8A',
      margin: '18px 0 8px',
    }}>
      {children}{count != null ? ` · ${count}` : ''}
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{
      textAlign: 'center', padding: '48px 24px', color: '#8A8A8A',
    }}>
      <div style={{
        fontFamily: 'var(--font-head)', fontSize: 18, fontWeight: 700,
        letterSpacing: '0.04em', textTransform: 'uppercase', color: '#5C5C5C',
      }}>Nothing here yet</div>
      <div style={{ fontSize: 14, marginTop: 8, lineHeight: 1.5 }}>
        Material you request will show up here, with where it has got to.
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function MyRequestsPage({ openRequestId, setOpenRequestId }) {
  const { profile } = useAuth();
  // #590 -- the Withdraw sheet: a bottom sheet on a phone and a carried tablet,
  // a dialog at a desk (#417 D24), and finger-sized where a finger is used.
  const { phoneLayout, coarse } = useTier();
  const [withdrawing, setWithdrawing] = useState(null);   // { req, offer }
  const { message, type, showSuccess, clearMessage } = useMessage();

  const [requests, setRequests] = useState([]);
  const [approvedByNames, setApprovedByNames] = useState({});
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('open');
  const [selectedId, setSelectedId] = useState(null);
  // #744 -- the request a notification named, ringed on a phone and brought
  // into view. Kept until the page is left: it answers "which one was it?".
  const [flashId, setFlashId] = useState(null);
  // #679 -- set when a card is opened from the keyboard, so the panel takes
  // the focus once it has rendered the request.
  const [focusPanel, setFocusPanel] = useState(false);
  const panelRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    function handler() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // D2 — the whole scoping rule is this one `.eq`, and it is the reason the
  // page is safe to give every role. No branch predicate: a person's own
  // requests are theirs wherever they raised them.
  //
  // D3 — no status filter at all, deliberately. The inbox's
  // `.in('status', ['pending','ordered'])` is precisely what makes it useless
  // to a requester, and re-adding any status filter here would reintroduce it.
  const load = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);

    const { data, error } = await supabase
      .from('material_requests')
      .select(`
        id, request_number, branch_id, requested_by, status, notes,
        department_id,
        created_at, updated_at, work_order_ref, po_ref,
        denial_reason, denial_reason_id, denial_reason_label, approved_by, approved_at,
        cancelled_by, cancelled_at, cancel_reason,
        tracking_carrier, tracking_number, tracking_eta,
        sent_as:branch_transfers!branch_transfers_from_request_id_fkey ( id, status, from_branch_id, transfer_number ),
        material_request_items (
          id, item_id, qty_requested, qty_received, notes,
          is_off_catalog, off_catalog_description, off_catalog_uom,
          tracking_carrier, tracking_number, tracking_eta,
          inventory_items ( id, name, sku, uom )
        )
      `)
      .eq('requested_by', profile.id)
      .order('created_at', { ascending: false })
      .order('id', { foreignTable: 'material_request_items', ascending: true });

    // #514 / gate D28 — transfers join the same list, shaped by
    // asRequestRow() in branchTransfers.js so this page reads one document
    // format rather than learning a second set of column names.
    //
    // FETCHED SEPARATELY AND MERGED IN MEMORY, not joined: they are different
    // tables with no relationship, and PostgREST cannot return them as one
    // result. Failure is independent on purpose — if transfers cannot be read,
    // the material requests still render rather than the page going blank.
    const { data: tData, error: tError } = await fetchMyTransfers(profile.id);
    if (tError) console.error('[#514] could not load transfers for My requests', tError);

    const rows = [
      ...(error ? [] : (data || [])),
      ...((tError ? [] : (tData || [])).map(asRequestRow)),
    ];
    setRequests(rows);

    // #391 D5's second half, enabled the same session. `approved_by` is now
    // written by the manager's approve, so an approved request can finally name
    // who approved it. Every request approved BEFORE 78n carries null, and this
    // is written to degrade rather than to assume: no name, no line.
    // #590 / #592 -- and whoever ended a request, so "Canceled by Jennifer K."
    // can name her. One map of names, by id, whichever role they played.
    const approverIds = Array.from(new Set(rows.flatMap(r => [r.approved_by, r.cancelled_by]).filter(Boolean)));
    if (approverIds.length) {
      const { data: people } = await supabase
        .from('users')
        .select('id, full_name')
        .in('id', approverIds);
      const byId = {};
      (people || []).forEach(p => { if (p.full_name) byId[p.id] = p.full_name; });
      setApprovedByNames(byId);
    } else {
      setApprovedByNames({});
    }

    // #396, Session 78n4 — the managers who could actually act on these
    // requests, so the waiting line can name a person again.
    //
    // Fetched for the branches these requests belong to rather than for the
    // requester's own branch: a request is almost always at the requester's
    // branch, but nothing guarantees it, and scoping to `profile.branch_id`
    // would silently print nothing for the exception.
    // #598 -- a transfer's first stage waits on the ASKING branch, so its
    // managers are fetched too.
    const branchIds = Array.from(new Set(rows.flatMap(r => [r.branch_id, r.__toBranchId]).filter(v => v != null)));
    if (branchIds.length) {
      const { data: mgrs } = await supabase
        .from('users')
        .select('id, full_name, branch_id, department_id, manager_scope, can_approve_requests, role, is_active')
        .eq('role', 'manager')
        .eq('is_active', true)
        // #643 -- ORDERED, because an unordered SELECT returns rows in whatever
        // order Postgres last wrote them. An unrelated edit to one manager's
        // profile rewrote their row and the same request named a different
        // colleague an hour later. The names below are built from this list, so
        // the order has to be a property of the query rather than of the heap.
        .order('full_name')
        .in('branch_id', branchIds);
      setManagers(mgrs || []);
    } else {
      setManagers([]);
    }

    setLoading(false);
  }, [profile]);

  useEffect(() => { load(); }, [load]);

  // D11 — the approved and denied notifications deep-link here now, carrying
  // the request id. Select it, and switch to whichever group holds it so the
  // row is not filtered out of view on desktop.
  useEffect(() => {
    if (!openRequestId || loading) return;
    const match = requests.find(r => r.id === openRequestId);
    if (match) {
      setTab(isOpenStatus(match.status) ? 'open' : 'finished');
      setSelectedId(match.id);
      setFlashId(match.id);
    }
    if (setOpenRequestId) setOpenRequestId(null);
  }, [openRequestId, loading, requests, setOpenRequestId]);

  // #744, Session 79.22b.6d -- ON A PHONE THE NAMED REQUEST IS BROUGHT TO THE
  // READER. The desk opens it in the panel; a phone has no panel, so the link
  // used to leave a person at the top of the list with the request seven
  // screens down and nothing marked. The inbox's pattern (#605): a second
  // effect, because the card must have rendered before it can be scrolled to.
  // #686's fault -- the page reloading after the scroll -- was fixed at its
  // source (AuthContext's keepIfUnchanged), and this page reads requests and
  // transfers inside ONE load() before `loading` goes false, so the scroll
  // never runs against half a list.
  useEffect(() => {
    if (!flashId || loading || !isMobile) return;
    const el = document.getElementById(`request-card-${flashId}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [flashId, loading, isMobile]);

  // #679 -- the keyboard lands in the panel once it shows the request, so the
  // next Tab reaches Withdraw rather than every card below the one opened.
  useEffect(() => {
    if (!focusPanel || !selectedId) return;
    if (panelRef.current) panelRef.current.focus();
    setFocusPanel(false);
  }, [focusPanel, selectedId]);

  function openFromKeyboard(id) {
    setSelectedId(id);
    setFocusPanel(true);
  }

  // Esc in the panel goes back to the card it shows.
  function panelKeyDown(e) {
    if (e.key !== 'Escape' || !selectedId) return;
    const card = document.getElementById(`request-card-${selectedId}`);
    if (card) { e.preventDefault(); card.focus(); }
  }

  function offerFor(r) {
    // #643 -- "AND", not "or". withdrawMaterialRequest() notifies the whole
    // approver set, so "A or B will be told" would understate what happens.
    // The waiting line says "or" for the opposite and equally true reason:
    // either of them can approve it.
    const approverName = approverPhrase(topApprovers(managers, waitingOn(r)), { join: 'and' });
    return withdrawOfferFor(r, { profile, approverName });
  }

  async function submitWithdraw(reason) {
    const { req, offer } = withdrawing;
    if (req.__kind === 'transfer') {
      return withdrawTransfer(req.__raw, {
        scope: offer.scope, reason, withdrawnBy: profile?.id || null,
        withdrawnByName: profile?.full_name || null, branchNames: BRANCH_NAMES_BY_ID,
      });
    }
    return withdrawMaterialRequest({ request: req, reason, askerName: profile?.full_name || null });
  }

  const open = requests.filter(r => isOpenStatus(r.status)).sort(sortOpen);
  const finished = requests.filter(r => FINISHED_STATUSES.includes(r.status)).sort(sortFinished);

  const selected = requests.find(r => r.id === selectedId) || null;

  if (loading) {
    return <div style={{ padding: 24, color: '#8A8A8A' }}>Loading your requests…</div>;
  }

  const withdrawSheet = withdrawing && (
    <WithdrawSheet
      visible
      phoneLayout={phoneLayout}
      coarse={coarse}
      title={withdrawing.offer.title}
      subtitle={withdrawing.offer.subtitle}
      consequence={withdrawing.offer.consequence}
      reasonLabel={withdrawing.offer.reasonLabel}
      blockedReason={withdrawing.offer.blockedReason}
      keepLabel={withdrawing.offer.keepLabel}
      submitLabel={withdrawing.offer.submitLabel}
      onClose={() => setWithdrawing(null)}
      onSubmit={submitWithdraw}
      onDone={() => {
        const who = withdrawing.offer.who;
        setWithdrawing(null);
        // 371 F2 Done: "Withdrawn. {branch} has been told."
        showSuccess(`Withdrawn. ${who} ${/( and |approvers$|managers$|executives$)/.test(who) ? 'have' : 'has'} been told.`);
        load();
      }}
    />
  );

  const header = (
    <div style={{ marginBottom: 4 }}>
      <div style={{
        fontFamily: 'var(--font-head)', fontSize: 22, fontWeight: 700,
        letterSpacing: '0.04em', textTransform: 'uppercase', color: '#1A1A1A',
      }}>My requests</div>
      <div style={{ fontSize: 13, color: '#5C5C5C', marginTop: 2 }}>
        {branchName(profile?.branch_id)}
      </div>
      <InlineMessage type={type} message={message} onDismiss={clearMessage} />
    </div>
  );

  // ── Mobile (D8's destination, and the priority surface) ────────────────────
  //
  // Callout D — Finished is a HEADING here, not a tab. A second tab would hide
  // the outcome behind a tap and re-create the thing that makes the approval
  // queue unusable for this reader. Scrolling costs less than a decision.
  //
  // Everything a person asks about is on the card, so this list is the whole
  // screen: no detail panel, no navigation, nothing to come back from. That is
  // the claim the gate makes against SAP, which has all of this and puts it
  // three or four taps into a desktop app.
  if (isMobile) {
    return (
      <div style={{ padding: '16px 14px 90px' }}>
        {header}
        {requests.length === 0 && <EmptyState />}
        {open.length > 0 && (
          <>
            <GroupHeading count={open.length}>Open</GroupHeading>
            {open.map(r => (
              <RequestCard key={r.id} req={r} managers={managers} approvedByNames={approvedByNames} expanded
                flash={flashId === r.id}
                offer={offerFor(r)} onWithdraw={(req, offer) => setWithdrawing({ req, offer })} />
            ))}
          </>
        )}
        {finished.length > 0 && (
          <>
            <GroupHeading count={finished.length}>Finished</GroupHeading>
            {finished.map(r => (
              <RequestCard key={r.id} req={r} managers={managers} approvedByNames={approvedByNames} expanded
                flash={flashId === r.id} />
            ))}
          </>
        )}
        {withdrawSheet}
      </div>
    );
  }

  // ── Desktop ───────────────────────────────────────────────────────────────
  //
  // Callout G — Open and Finished are tabs here and headings on the phone, and
  // that is deliberate rather than an inconsistency. A desktop list has room for
  // a persistent count beside each tab; a phone does not, and a tap costs more
  // there. The grouping is identical either way.
  const list = tab === 'open' ? open : finished;

  return (
    <div style={{ padding: '20px 24px' }}>
      {header}

      <div style={{ display: 'flex', gap: 4, margin: '16px 0 14px', borderBottom: '1px solid #E4E4E4' }}>
        {[['open', 'Open', open.length], ['finished', 'Finished', finished.length]].map(([key, label, count]) => (
          <button
            key={key}
            onClick={() => { setTab(key); setSelectedId(null); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '8px 14px', fontSize: 14, fontWeight: tab === key ? 700 : 500,
              color: tab === key ? INVENTORY_GREEN : '#6A6A6A',
              borderBottom: tab === key ? `2px solid ${INVENTORY_GREEN}` : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {label} · {count}
          </button>
        ))}
      </div>

      {requests.length === 0 ? <EmptyState /> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 420px) 1fr', gap: 20, alignItems: 'start' }}>
          <div>
            {list.length === 0 && (
              <div style={{ color: '#8A8A8A', fontSize: 14, padding: '12px 0' }}>
                Nothing in this group.
              </div>
            )}
            {list.map(r => (
              <RequestCard
                key={r.id}
                req={r}
                managers={managers}
                approvedByNames={approvedByNames}
                onSelect={() => setSelectedId(r.id)}
                onKeySelect={() => openFromKeyboard(r.id)}
                selected={selectedId === r.id}
              />
            ))}
          </div>

          {/* #679 -- the panel takes the focus when a card is opened from the
              keyboard (tabIndex -1: reachable by the page, not a Tab stop of
              its own), and Esc returns to the card. No ring on the panel
              itself: the approved drawing rings the card and Withdraw. */}
          <div
            ref={panelRef}
            tabIndex={-1}
            aria-label="The request you opened"
            onKeyDown={panelKeyDown}
            style={{
              background: '#FFFFFF', border: '1px solid #E4E4E4', borderRadius: 10,
              padding: 18, minHeight: 200, outline: 'none',
            }}>
            {selected ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div>
                    <div style={{
                      fontFamily: 'var(--font-head)', fontSize: 16, fontWeight: 700,
                      letterSpacing: '0.05em', color: '#1A1A1A',
                    }}>{selected.request_number}</div>
                    <div style={{ fontSize: 13, color: '#8A8A8A', marginTop: 3 }}>
                      Raised {fmtDate(selected.created_at)}
                      {selected.work_order_ref ? ` · WO# ${selected.work_order_ref}` : ''}
                      {` · ${branchName(selected.branch_id)}`}
                    </div>
                  </div>
                  <StatusPill status={selected.status}
                    label={endedPresentation(selected, approvedByNames)?.pill
                      || (sentAsTransferOf(selected) ? 'Sent as a transfer' : null)
                      || (selected.__own ? ownShelfLabel(selected) : null)} />
                </div>

                {/* #597 -- the same block the phone's card renders, so the two
                    halves of this screen cannot disagree again. */}
                <RequestStatusBlock req={selected} managers={managers} approvedByNames={approvedByNames} />

                <AskedForBlock req={selected} />
                <WithdrawFoot req={selected} offer={offerFor(selected)}
                  onWithdraw={(req, offer) => setWithdrawing({ req, offer })} />
              </>
            ) : (
              <div style={{ color: '#8A8A8A', fontSize: 14 }}>
                Select a request to see what you asked for and where it has got to.
              </div>
            )}
          </div>
        </div>
      )}
      {withdrawSheet}
    </div>
  );
}
